<script setup>
  import { computed, onMounted, ref } from "vue";
  import { storeToRefs } from "pinia";
  import ThreePanePage from "@templates/ThreePanePage";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { FormItem } from "@/components/ui/form";
  import { Label } from "@/components/ui/label";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
  import { useMCampaignStore } from "@stores/mCampaign";
  import { useUnsavedChangesGuard } from "@/utils/composables/useUnsavedChangesGuard";
  import { useMCampaignUnsavedChanges } from "@/utils/composables/useMCampaignUnsavedChanges";
  import { useSurveyOptions } from "@utils/composables/filters/useSurveyOptions";
  import { useSurveysStore } from "@stores/surveys";
  import { useAdsPagesStore } from "@stores/adsPages";
  import { useAdAccountsStore } from "@stores/adAccounts";
  import { useMCreativeStore } from "@stores/mCreative";

  const mCampaignStore = useMCampaignStore();
  const surveysStore = useSurveysStore();
  const adsPagesStore = useAdsPagesStore();
  const adAccountsStore = useAdAccountsStore();

  const { currentMCampaign, currentQuotasFullStatus, quotasFullStatusOptions, isAlreadyPushed } =
    storeToRefs(mCampaignStore);
  const {
    bidderStatusOptions,
    autostopStatusOptions,
    nightbreakStatusOptions,
    quotasFullLevelOptions,
    updateQuotasFullStatus,
  } = mCampaignStore;

  const { lightSurvey } = storeToRefs(surveysStore);
  const { getSurveyLight, selectLightSurvey } = surveysStore;

  const { bindings: surveyPageBindings, load: loadSurveyOptions } = useSurveyOptions();

  const surveyOptionsBindings = computed(() => {
    const bindings = surveyPageBindings.value;
    if (!lightSurvey.value?.id || bindings.options.some((o) => o.value === lightSurvey.value.id)) return bindings;
    return { ...bindings, options: [{ label: lightSurvey.value.name, value: lightSurvey.value.id }, ...bindings.options] };
  });

  const { adAccountOptions } = storeToRefs(adAccountsStore);
  const { fetchAdAccounts } = adAccountsStore;

  const { facebookPageOptions, instagramPageOptions } = storeToRefs(adsPagesStore);
  const { fetchAdsPages } = adsPagesStore;

  const mCreativeStore = useMCreativeStore();
  const { resetUrl, replaceDefaultSubtitles } = mCreativeStore;

  useUnsavedChangesGuard(useMCampaignUnsavedChanges().hasUnsavedChanges);

  const settingsCollapse = ref([]);

  const handleSurveySelect = (value) => {
    currentMCampaign.value.survey_id = value;

    selectLightSurvey(value);

    currentMCampaign.value.m_creatives.forEach((creative) => {
      resetUrl(creative);
      replaceDefaultSubtitles(creative);
    });
  };

  onMounted(async () => {
    const surveyId = currentMCampaign.value.survey_id;
    await Promise.all([getSurveyLight(surveyId), loadSurveyOptions(1), fetchAdsPages(), fetchAdAccounts()]);
  });
</script>

<template>
  <ThreePanePage data-cy="master-campaign-general-page">
    <template #center>
      <Card class="p-6">
        <CardHeader class="pb-6">
          <CardTitle class="heading-page">{{ currentMCampaign.name }}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-6">
          <form
            class="space-y-6"
            @submit.prevent>
            <FormItem>
              <Label for="mcampaign-name">Master campaign's name</Label>
              <Input
                id="mcampaign-name"
                v-model="currentMCampaign.name" />
            </FormItem>
            <FormItem>
              <Label for="mcampaign-survey">Survey</Label>
              <Select
                id="mcampaign-survey"
                v-bind="surveyOptionsBindings"
                :model-value="currentMCampaign.survey_id"
                :disabled="isAlreadyPushed"
                placeholder="Search..."
                @update:model-value="handleSurveySelect" />
            </FormItem>
            <FormItem>
              <Label for="mcampaign-adaccount">Advertiser account</Label>
              <Select
                id="mcampaign-adaccount"
                v-model="currentMCampaign.resources.facebook.adaccount"
                :options="adAccountOptions"
                :disabled="isAlreadyPushed" />
            </FormItem>
            <FormItem>
              <Label for="mcampaign-fb-page">Facebook page</Label>
              <Select
                id="mcampaign-fb-page"
                v-model="currentMCampaign.pages.facebook"
                :options="facebookPageOptions" />
            </FormItem>
            <FormItem>
              <Label for="mcampaign-ig-page">Instagram page</Label>
              <Select
                id="mcampaign-ig-page"
                v-model="currentMCampaign.pages.instagram"
                :options="instagramPageOptions" />
            </FormItem>
            <FormItem>
              <Label for="mcampaign-crm-id">CRM ID</Label>
              <Input
                id="mcampaign-crm-id"
                v-model="currentMCampaign.crm_id" />
            </FormItem>

            <Accordion
              v-model="settingsCollapse"
              type="multiple">
              <AccordionItem value="settings">
                <AccordionTrigger class="text-base font-semibold">Bidder settings</AccordionTrigger>
                <AccordionContent class="space-y-6 pt-2">
                  <FormItem>
                    <Label for="mcampaign-bidder-status">Bidder mode</Label>
                    <Select
                      id="mcampaign-bidder-status"
                      v-model="currentMCampaign.bidder_status"
                      :options="bidderStatusOptions" />
                  </FormItem>
                  <FormItem>
                    <Label for="mcampaign-autostop-status">Autostop</Label>
                    <Select
                      id="mcampaign-autostop-status"
                      v-model="currentMCampaign.autostop_status"
                      :options="autostopStatusOptions" />
                  </FormItem>
                  <FormItem>
                    <Label for="mcampaign-nightbreak-status">Nightbreak</Label>
                    <Select
                      id="mcampaign-nightbreak-status"
                      v-model="currentMCampaign.night_break_status"
                      :options="nightbreakStatusOptions" />
                  </FormItem>
                  <FormItem>
                    <Label for="mcampaign-budget-schedule-pct">Budget schedule increase (%)</Label>
                    <Input
                      id="mcampaign-budget-schedule-pct"
                      v-model.number="currentMCampaign.budget_schedule_increase_pct"
                      type="number"
                      min="0"
                      max="200" />
                  </FormItem>
                  <FormItem>
                    <Label>When quotas full ...</Label>
                    <div class="flex items-center gap-3">
                      <span class="text-sm text-muted-foreground">At</span>
                      <Select
                        v-model="currentMCampaign.alerts.quota_full.level"
                        :options="quotasFullLevelOptions"
                        class="flex-1" />
                      <span class="text-sm text-muted-foreground">Do</span>
                      <Select
                        :model-value="currentQuotasFullStatus"
                        :options="quotasFullStatusOptions"
                        class="flex-1"
                        @update:model-value="updateQuotasFullStatus" />
                    </div>
                  </FormItem>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </form>
        </CardContent>
      </Card>
    </template>
  </ThreePanePage>
</template>
